// Input validation utilities
export const validateEmail = (email: string): boolean => {
  if (email.length > 254) return false
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export const validateUsername = (username: string): { valid: boolean; error?: string } => {
  if (!username || username.trim().length === 0) {
    return { valid: false, error: 'Username is required' }
  }

  if (username.length < 3) {
    return { valid: false, error: 'Username must be at least 3 characters' }
  }

  if (username.length > 20) {
    return { valid: false, error: 'Username must be less than 20 characters' }
  }

  const usernameRegex = /^[a-zA-Z0-9_-]+$/
  if (!usernameRegex.test(username)) {
    return { valid: false, error: 'Username can only contain letters, numbers, underscores, and hyphens' }
  }

  return { valid: true }
}

export const validatePassword = (password: string): { valid: boolean; error?: string } => {
  if (!password || password.length === 0) {
    return { valid: false, error: 'Password is required' }
  }

  if (password.length < 8) {
    return { valid: false, error: 'Password must be at least 8 characters' }
  }

  if (password.length > 128) {
    return { valid: false, error: 'Password must be less than 128 characters' }
  }

  // Check for at least one letter and one number
  const hasLetter = /[a-zA-Z]/.test(password)
  const hasNumber = /[0-9]/.test(password)

  if (!hasLetter || !hasNumber) {
    return { valid: false, error: 'Password must contain at least one letter and one number' }
  }

  return { valid: true }
}

// Sanitize user input to prevent XSS and injection attacks
export const sanitizeInput = (input: string): string => {
  return input
    .trim()
    // Strip HTML tags and encoded entities
    .replace(/[<>'"]/g, '')
    // Remove javascript: protocol
    .replace(/javascript\s*:/gi, '')
    // Remove event handler patterns
    .replace(/on\w+\s*=/gi, '')
    // Remove script tags (in case partial match)
    .replace(/<script\b[^>]*>(.*?)<\/script>/gi, '')
    // Strip SQL injection patterns
    .replace(/(\b(SELECT|INSERT|UPDATE|DELETE|DROP|UNION|ALTER|CREATE|EXEC|EXECUTE|xp_)\b)/gi, '')
    .replace(/(--|;|\/\*|\*\/|'|")/g, '')
}

// Sanitize email specifically - more permissive but still safe
export const sanitizeEmail = (email: string): string => {
  return email
    .trim()
    .toLowerCase()
    // Only allow valid email characters
    .replace(/[^a-z0-9@._+-]/g, '')
}

// Validate URLs to prevent javascript:, data:, and other protocol attacks
export const isValidUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url)
    return ['http:', 'https:'].includes(parsed.protocol)
  } catch {
    return false
  }
}

// Validate that a link is a safe relative path (for internal navigation)
export const isSafeRelativePath = (path: string): boolean => {
  return path.startsWith('/') && !path.startsWith('//') && !path.includes(':')
}
